/**
 * Feedback Service
 * Handles submission of user feedback to the backend API
 */

// Load API URL from aws-exports.json
let FEEDBACK_API_URL = ""

// Dynamically load the API URL from aws-exports.json
async function loadApiUrl(): Promise<string> {
  if (FEEDBACK_API_URL) {
    return FEEDBACK_API_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    FEEDBACK_API_URL = config.feedbackApiUrl ? `${config.feedbackApiUrl}feedback` : ""
    return FEEDBACK_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Feedback API URL not configured")
  }
}

export interface FeedbackPayload {
  sessionId: string
  message: string
  /**
   * The user turn that produced `message` — the contract's `question` field.
   *
   * Always sent, even as "": the Lambda reads it as `body.get('question', '')`,
   * so an absent key is written as an empty string and the call still returns
   * 200. Without the field there is no way to tell "this turn had no question"
   * apart from "the client stopped sending it" — which is exactly what hid the
   * previous omission for a month.
   */
  question: string
  feedbackType: "positive" | "negative"
  comment?: string
}

export interface FeedbackResponse {
  success: boolean
  feedbackId: string
}

/**
 * Submit feedback to the backend API
 *
 * @param payload - Feedback data in camelCase format
 * @param idToken - Cognito ID token for authentication (required by API Gateway Cognito authorizer)
 * @returns Promise with feedback response
 */
export async function submitFeedback(
  payload: FeedbackPayload,
  idToken: string
): Promise<FeedbackResponse> {
  try {
    // Load the API URL dynamically
    const apiUrl = await loadApiUrl()

    // Map the UI payload to the backend feedback contract (the Lambda + weekly
    // report key off `rating`/`question`/`session_id`/`answer`, not the UI's
    // camelCase). These five fields ARE the whole contract: the Lambda fills in
    // a default for anything missing, so dropping one does not fail — it writes
    // a blank. `src/test/feedbackService.contract.test.ts` asserts the body
    // field by field so a future remap cannot silently lose one again.
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        rating: payload.feedbackType,
        comment: payload.comment ?? "",
        question: payload.question,
        answer: payload.message,
        session_id: payload.sessionId,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    const data: FeedbackResponse = await response.json()
    return data
  } catch (error) {
    console.error("Error submitting feedback:", error)
    throw error
  }
}
